// src/redux/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authService } from '../../services/authService.js'
import { handleApiError } from '../../utils/handleApiError.js'
import { toast } from 'react-hot-toast'

// Helper function to check and parse localStorage safely
const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem('user')
    const token = localStorage.getItem('token')

    // If no token, clear everything
    if (!token) {
      localStorage.removeItem('user')
      return null
    }

    return userStr ? JSON.parse(userStr) : null
  } catch (error) {
    console.error('Failed to parse user from localStorage:', error)
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    return null
  }
}

export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)

      // Check response structure
      if (!response || !response.data) {
        throw new Error('Invalid server response')
      }

      const { token, user } = response.data

      if (token && user) {
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(user))
        toast.success('Login successful!')
        return { token, user }
      } else {
        throw new Error('Invalid response structure from server')
      }
    } catch (error) {
      const errorMessage = handleApiError(error)
      toast.error(errorMessage)
      return rejectWithValue(errorMessage)
    }
  }
)

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getCurrentUser()
      return response.data?.user || response.data
    } catch (error) {
      const errorMessage = handleApiError(error)
      // Don't show toast for 401 (handled by interceptor)
      if (error.response?.status !== 401) {
        toast.error(errorMessage)
      }
      return rejectWithValue(errorMessage)
    }
  }
)

export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await authService.getCurrentUser()
      return response.data?.user || response.data
    } catch (error) {
      // Clear invalid auth data
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      return rejectWithValue(handleApiError(error))
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    error: null,
    initialized: false
  },
  reducers: {
    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      toast.success('Logged out successfully')
    },
    clearError: (state) => {
      state.error = null
    },
    initializeAuth: (state) => {
      const token = localStorage.getItem('token')
      const user = getStoredUser()

      state.token = token
      state.user = user
      state.isAuthenticated = !!(token && user)
      state.initialized = true
    },
    setAuthState: (state, action) => {
      const { user, token } = action.payload
      state.user = user
      state.token = token
      state.isAuthenticated = !!(user && token)
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
        state.initialized = true
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.isAuthenticated = false
        state.initialized = true
      })
      // Validate Token
      .addCase(validateToken.pending, (state) => {
        state.loading = true
      })
      .addCase(validateToken.fulfilled, (state, action) => {
        state.loading = false
        state.user = action.payload
        state.isAuthenticated = true
        state.initialized = true
      })
      .addCase(validateToken.rejected, (state) => {
        state.loading = false
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.initialized = true
      })
  }
})

export const { logout, clearError, initializeAuth, setAuthState } = authSlice.actions
export default authSlice.reducer